import type { BreakpointId } from '@wix/document-services-types'
import type { CssNode, CssDeclaration, CssRoot, CssRule } from '../types'
import { buildCssRoot, buildCssRule } from '../utils'
import type { BreakpointsWithOwnerMap } from '../../breakpoints'
import { buildBreakpointAst, buildVariablesLayer } from './utils'

export type CssRecordType = 'style' | 'variable'
export interface CssRecord<T extends CssRecordType = CssRecordType> {
    selector: string
    breakpointId?: BreakpointId
    type: T
    declarations: CssDeclaration[]
}

const groupRecordsByBreakpointAndType = (
    records: CssRecord[],
): Record<BreakpointId, Record<CssRecordType, CssRecord[]>> => {
    const grouped: Record<BreakpointId, Record<CssRecordType, CssRecord[]>> = {}

    for (const record of records) {
        const breakpointKey = record.breakpointId ?? 'default'
        if (!grouped[breakpointKey]) {
            grouped[breakpointKey] = { style: [], variable: [] }
        }
        grouped[breakpointKey][record.type].push(record)
    }

    return grouped
}

const buildRulesFromRecords = (records: CssRecord[]): CssRule[] => {
    const rules: CssRule[] = []

    for (const record of records) {
        if (record.declarations.length === 0) {
            continue
        }
        rules.push(buildCssRule(record.selector, record.declarations))
    }

    return rules
}

const buildNodesFromRecordGroup = (group: Record<CssRecordType, CssRecord[]>): CssNode[] => {
    const nodes: CssNode[] = buildRulesFromRecords(group.style)

    const variableRules = buildRulesFromRecords(group.variable)
    if (variableRules.length > 0) {
        nodes.push(buildVariablesLayer(variableRules))
    }

    return nodes
}

export const buildCssAst = (records: CssRecord[], breakpoints: BreakpointsWithOwnerMap): CssRoot => {
    const cssRoot = buildCssRoot()
    const grouped = groupRecordsByBreakpointAndType(records)

    if (grouped.default) {
        cssRoot.nodes.push(...buildNodesFromRecordGroup(grouped.default))
    }

    for (const [breakpointId, breakpoint] of Object.entries(breakpoints)) {
        const breakpointGroup = grouped[breakpointId]
        if (!breakpointGroup) {
            continue
        }

        const blocks = buildNodesFromRecordGroup(breakpointGroup)
        if (blocks.length === 0) {
            continue
        }

        cssRoot.nodes.push(buildBreakpointAst(blocks, breakpoint))
    }

    return cssRoot
}
